import { Fragment } from 'react';
import { WeekPreview, WeekPreviewDay, WeekPreviewRow } from '@wundo/ui';
import {
  formatDayAndMonth,
  formatTime,
  formatWeekdayShort,
} from '@/components/agenda/meetingFormat.ts';
import { isSameDay } from '@/lib/week.ts';
import { m } from '@/paraglide/messages.js';

export interface PlannedMeeting {
  id: string;
  title: string;
  startsAt: Date;
}

export interface PlannedMeetingsPreviewProps {
  meetings: PlannedMeeting[];
}

/**
 * What the user has typed so far, drawn as Wundo will hold it.
 *
 * The wizard's other preview shows the server's answer; this one cannot, because
 * nothing has been sent yet. It is deliberately the same object — the same
 * frame, the same rows — so the step reads as filling an agenda rather than as
 * filling a form, and every row is marked: a conversation somebody typed in by
 * hand is one Wundo takes on, with no classification to wait for.
 */
export function PlannedMeetingsPreview({ meetings }: PlannedMeetingsPreviewProps) {
  const inOrder = [...meetings].sort(
    (one, other) => one.startsAt.getTime() - other.startsAt.getTime(),
  );

  return (
    <WeekPreview
      title={m.onboarding_first_meeting_preview_title()}
      range={m.onboarding_first_meeting_preview_range()}
    >
      {inOrder.length === 0 && (
        <p className='px-1 py-4 text-center text-sm text-ink-muted'>
          {m.onboarding_first_meeting_preview_empty()}
        </p>
      )}

      {inOrder.map((meeting, index) => {
        /* The day is written once per run of rows, never per row. */
        const previous = inOrder[index - 1];
        const opensADay =
          previous === undefined || !isSameDay(previous.startsAt, meeting.startsAt);

        return (
          <Fragment key={meeting.id}>
            {opensADay && (
              <WeekPreviewDay>
                {`${formatWeekdayShort(meeting.startsAt)} ${formatDayAndMonth(meeting.startsAt)}`}
              </WeekPreviewDay>
            )}
            <WeekPreviewRow
              time={formatTime(meeting.startsAt.toISOString())}
              title={meeting.title}
              marked
            />
          </Fragment>
        );
      })}
    </WeekPreview>
  );
}
